import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Palette, Dices as Devices, Gauge, Search, Shield, Code } from 'lucide-react';
import PageTransition from '../../components/ui/PageTransition';
import HeroSection from '../../components/sections/HeroSection';
import SectionHeading from '../../components/ui/SectionHeading';
import CtaSection from '../../components/sections/CtaSection';
import Button from '../../components/ui/Button';

const WebDesignPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Web Design Services | Consiliuma - Nottingham Web Design Experts</title>
        <meta name="description" content="Professional web design services in Nottingham. Creating beautiful, responsive websites that convert visitors into customers. British expertise with global reach." />
        <meta name="keywords" content="Web Design Nottingham, Professional Website Design, Responsive Web Design, UI/UX Design, Business Website Design" />
        <link rel="canonical" href="/services/web-design" />
      </Helmet>

      <HeroSection
        title="Expert Web Design Services"
        subtitle="Beautiful, responsive websites that drive results and enhance your brand"
        ctaText="Get Started"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg"
      />

      {/* Core Features */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Our Web Design Approach"
            subtitle="We create websites that not only look beautiful but also drive business results"
            center
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Palette size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Beautiful Design</h3>
              <p className="text-secondary">
                We create visually stunning websites that reflect your brand identity and engage your audience effectively.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Devices size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Responsive Design</h3>
              <p className="text-secondary">
                Every website we create works flawlessly across all devices, from smartphones to desktop computers.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Gauge size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Performance</h3>
              <p className="text-secondary">
                Fast-loading, optimized websites that provide excellent user experience and improve conversion rates.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Search size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">SEO-Friendly</h3>
              <p className="text-secondary">
                Built with search engines in mind to help improve your visibility and attract more organic traffic.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Shield size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Security</h3>
              <p className="text-secondary">
                Secure by design with SSL certificates and modern security practices to protect your website and users.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Code size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Clean Code</h3>
              <p className="text-secondary">
                Well-structured, maintainable code that ensures your website remains easy to update and scale.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Our Design Process"
                subtitle="A proven approach to creating exceptional websites"
              />
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Discovery</h3>
                    <p className="text-secondary">
                      We begin by understanding your business, goals, target audience, and specific requirements to ensure our design aligns with your objectives.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Planning</h3>
                    <p className="text-secondary">
                      Creating wireframes and prototypes to establish the perfect structure and user flow for your website.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Design</h3>
                    <p className="text-secondary">
                      Creating beautiful, brand-aligned visual designs with attention to every detail and user experience consideration.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Development</h3>
                    <p className="text-secondary">
                      Building your website with clean, efficient code and implementing all functionality and features.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Testing</h3>
                    <p className="text-secondary">
                      Thorough testing across devices and browsers to ensure everything works perfectly.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Launch</h3>
                    <p className="text-secondary">
                      Carefully deploying your website and providing training and documentation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button to="/quote" variant="primary">
                  Start Your Project
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/1181298/pexels-photo-1181298.jpeg"
                alt="Web design process"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Types of Websites */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Types of Websites We Design"
            subtitle="Solutions tailored to your specific needs"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Business Websites</h3>
              <p className="text-secondary mb-4">
                Professional websites that showcase your business and generate leads.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Brand-focused design</li>
                <li>• Lead generation forms</li>
                <li>• Service showcases</li>
                <li>• Contact integration</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">E-commerce Websites</h3>
              <p className="text-secondary mb-4">
                Online stores that make selling products easy and efficient.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Product catalogs</li>
                <li>• Secure checkout</li>
                <li>• Inventory management</li>
                <li>• Payment integration</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Portfolio Websites</h3>
              <p className="text-secondary mb-4">
                Showcase your work with beautiful, engaging designs.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Project galleries</li>
                <li>• Case studies</li>
                <li>• Visual storytelling</li>
                <li>• Client testimonials</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Start Your Web Design Project?"
        subtitle="Let's create something exceptional together"
        buttonText="Get a Free Quote"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default WebDesignPage;